// ช่องเลือกที่พิมพ์ค้นได้ — ใช้แทน <select> เมื่อรายการยาวเกินกวาดตาหา
//
// <select> ของเบราว์เซอร์ค้นได้แค่ตัวอักษรแรก รายชื่อแคมเปญ KOL กับ project ฝั่ง
// Biddable มีหลายสิบรายการและขึ้นต้นด้วยรหัสปีเหมือนกันหมด (P2026-…) การกวาดตาหา
// จึงช้ากว่าพิมพ์ชื่อแบรนด์ที่จำได้
//
// ไม่ใช้ <datalist> เพราะ Safari ไม่ยอมกรองตามที่พิมพ์ และสั่งสไตล์ไม่ได้เลย
import { useId, useRef, useState } from "react";
import { cn } from "@shared/lib/utils";

export type SearchOption = {
  value: string;
  /** ข้อความที่แสดงและใช้ค้น — ใส่ทั้งรหัสและชื่อมาได้เลย */
  label: string;
};

type Props = {
  options: SearchOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** ข้อความเมื่อพิมพ์แล้วไม่ตรงอะไรเลย */
  emptyText?: string;
};

export function SearchSelect({
  options,
  value,
  onChange,
  placeholder = "พิมพ์เพื่อค้นหา…",
  className,
  emptyText = "ไม่พบรายการที่ตรงกับที่พิมพ์",
}: Props) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const picked = options.find((o) => o.value === value);
  // ปิดอยู่ = โชว์สิ่งที่เลือกไว้ · เปิดอยู่ = โชว์สิ่งที่พิมพ์
  const text = open ? query : (picked?.label ?? "");
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  // ทุกคำต้องอยู่ใน label — พิมพ์ "kirei kitchen" ก็เจอ แม้สองคำไม่ติดกัน
  const shown = terms.length
    ? options.filter((o) => {
        const hay = o.label.toLowerCase();
        return terms.every((t) => hay.includes(t));
      })
    : options;

  const commit = (opt: SearchOption) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const move = (by: number) => {
    if (!shown.length) return;
    setCursor((c) => Math.min(shown.length - 1, Math.max(0, c + by)));
  };

  return (
    <div
      className={cn("ss", className)}
      ref={boxRef}
      // ย้ายโฟกัสออกจากกล่องทั้งก้อน = ปิด · เช็ค relatedTarget เพราะการกดรายการ
      // ในลิสต์ก็ทำให้ input เสียโฟกัสชั่วขณะ ถ้าปิดทันทีจะกดเลือกไม่ได้
      onBlur={(e) => {
        if (!boxRef.current?.contains(e.relatedTarget as Node | null)) {
          setOpen(false);
          setQuery("");
        }
      }}
    >
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        className="ss-input"
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setQuery(e.target.value);
          setCursor(0);
          setOpen(true);
        }}
        onFocus={(e) => {
          // เปิดมาพร้อมชื่อที่เลือกไว้และคลุมดำไว้ทั้งบรรทัด — พิมพ์ทับได้ทันที
          // แต่ยังเห็นว่าตอนนี้เลือกอะไรอยู่ · ถ้าล้างเป็นค่าว่างเลย คนจะไม่รู้ว่า
          // ค่าเดิมคืออะไร และถ้าปล่อยชื่อเดิมไว้เฉย ๆ ตัวอักษรที่พิมพ์จะไปต่อท้าย
          // ชื่อนั้นจนค้นไม่เจออะไรเลย
          setQuery(picked?.label ?? "");
          setOpen(true);
          e.currentTarget.select();
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) setOpen(true);
            else move(1);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            move(-1);
          } else if (e.key === "Enter") {
            // ปล่อยให้ form submit ตามปกติเมื่อยังไม่ได้เปิดรายการ
            if (open && shown[cursor]) {
              e.preventDefault();
              commit(shown[cursor]);
            }
          } else if (e.key === "Escape") {
            setOpen(false);
            setQuery("");
          }
        }}
      />

      {open && (
        <div className="ss-pop" id={listId} role="listbox">
          {shown.length ? (
            shown.map((o, i) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={cn("ss-opt", i === cursor && "on", o.value === value && "picked")}
                onMouseEnter={() => setCursor(i)}
                onClick={() => commit(o)}
              >
                {o.label}
              </button>
            ))
          ) : (
            <div className="ss-none muted">{emptyText}</div>
          )}
        </div>
      )}
    </div>
  );
}
